import { useQuery } from '@apollo/client';
import { GetLatestOversightRatingsByRiskIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import { extractCmaCreatedAt, isLater } from '@/utils/oversightUtils';

export type OversightLeafRating = {
  Rating: number | null;
  Impact: number | null;
  Likelihood: number | null;
  ConfigId: string | null;
};

export type OversightRatingsByRisk = Map<
  string,
  { inherent?: OversightLeafRating; residual?: OversightLeafRating }
>;

const EMPTY_MAP: OversightRatingsByRisk = new Map();

/**
 * Fetches the latest oversight inherent + residual rating per risk for the given
 * risk Ids, keyed by RiskId.
 *
 * "Latest" is determined server-side by ordering each leaf's CMA-side parent rows
 * by `complianceMonitoringAssessment.CreatedAtTimestamp desc_nulls_last` and
 * picking the first. Across multiple leaves per risk, the client picks the leaf
 * whose top CMA was most recently created.
 *
 * Skips the query when oversight is disabled or no risks are visible.
 */
export const useLatestOversightRatingsByRisks = (params: {
  riskIds: string[];
  isEnabled: boolean;
}): { ratingsByRisk: OversightRatingsByRisk; loading: boolean } => {
  const { riskIds, isEnabled } = params;
  const skip = !isEnabled || riskIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightRatingsByRiskIdsDocument,
    {
      skip,
      variables: { RiskIds: riskIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const ratingsByRisk = useMemo<OversightRatingsByRisk>(() => {
    if (!data) {
      return EMPTY_MAP;
    }

    const latestByRisk = new Map<
      string,
      {
        inherent?: { leaf: OversightLeafRating; createdAt: string | null };
        residual?: { leaf: OversightLeafRating; createdAt: string | null };
      }
    >();

    for (const row of data.risk_uncontrolled_second_line_result) {
      const createdAt = extractCmaCreatedAt(row.cmaParent);
      const leaf: OversightLeafRating = {
        Rating: row.Rating ?? null,
        Impact: row.Impact ?? null,
        Likelihood: row.Likelihood ?? null,
        ConfigId: row.ConfigId ?? null,
      };
      // A finding can attach to multiple risks (insertChildRiskSecondLineResult
      // takes RiskIds[]). `riskParent` is server-filtered to ParentId ∈ $RiskIds,
      // so every entry maps to a risk we want to surface the rating against.
      for (const rp of row.riskParent) {
        const existing = latestByRisk.get(rp.ParentId) ?? {};
        if (
          !existing.inherent ||
          isLater(existing.inherent.createdAt, createdAt)
        ) {
          existing.inherent = { leaf, createdAt };
        }
        latestByRisk.set(rp.ParentId, existing);
      }
    }

    for (const row of data.risk_controlled_second_line_result) {
      const createdAt = extractCmaCreatedAt(row.cmaParent);
      const leaf: OversightLeafRating = {
        Rating: row.Rating ?? null,
        Impact: row.Impact ?? null,
        Likelihood: row.Likelihood ?? null,
        ConfigId: row.ConfigId ?? null,
      };
      for (const rp of row.riskParent) {
        const existing = latestByRisk.get(rp.ParentId) ?? {};
        if (
          !existing.residual ||
          isLater(existing.residual.createdAt, createdAt)
        ) {
          existing.residual = { leaf, createdAt };
        }
        latestByRisk.set(rp.ParentId, existing);
      }
    }

    const result: OversightRatingsByRisk = new Map();
    for (const [id, entry] of latestByRisk) {
      result.set(id, {
        inherent: entry.inherent?.leaf,
        residual: entry.residual?.leaf,
      });
    }

    return result;
  }, [data]);

  return { ratingsByRisk, loading };
};
